import React, { useEffect, useState, useRef, useCallback } from 'react';
import { RefreshCw, Check, Trophy, AlertCircle, Bike, Database, Zap } from 'lucide-react';
import { getStravaStatus, getStravaAuthUrl, syncStravaSSE, updateUsername, checkUsername } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

function SyncProgress({ progress }) {
  const pct = progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <div className="sync-progress">
      <div className="sync-progress__header">
        <span className="sync-progress__label">
          {progress.phase === 'fetching' ? 'CONNECTING TO STRAVA' : 'PROCESSING RIDES'}
        </span>
        <span className="sync-progress__count">
          {progress.phase === 'fetching' ? '' : `${progress.processed}/${progress.total}`}
        </span>
      </div>

      <div className="progress-bar" style={{ marginBottom: '14px' }}>
        <div
          className="progress-bar__fill"
          style={{
            width: progress.phase === 'fetching' ? '100%' : `${pct}%`,
            transition: 'width 0.3s ease',
            animation: progress.phase === 'fetching' ? 'pulse 1.5s ease-in-out infinite' : 'none',
            opacity: progress.phase === 'fetching' ? 0.5 : 1,
          }}
        />
      </div>

      {progress.current && (
        <div className="sync-progress__current">
          {progress.skipped
            ? <Zap size={12} style={{ flexShrink: 0 }} />
            : progress.cached
              ? <Database size={12} style={{ flexShrink: 0 }} />
              : <Bike size={12} style={{ flexShrink: 0 }} />
          }
          <span className="sync-progress__current-name">
            {progress.current}
          </span>
          {(progress.cached || progress.skipped) && (
            <span className="pill" style={{ background: 'var(--neutral-100)', border: '1px solid var(--border)', flexShrink: 0 }}>
              {progress.skipped ? 'no match' : 'cached'}
            </span>
          )}
        </div>
      )}

      {progress.matchedSoFar > 0 && (
        <div className="sync-progress__matched">
          <Trophy size={12} />
          {progress.matchedSoFar} trail{progress.matchedSoFar !== 1 ? 's' : ''} matched so far
        </div>
      )}
    </div>
  );
}

function UsernameSection() {
  const { user, setUser } = useAuth();
  const toast = useToast();
  const [username, setUsernameVal] = useState(user?.username || '');
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const checkAvailability = useCallback((value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const clean = value.toLowerCase().trim();

    if (clean.length < 3 || !/^[a-z0-9_]+$/.test(clean)) {
      setAvailable(null);
      setChecking(false);
      return;
    }

    if (clean === user?.username) {
      setAvailable(null);
      setChecking(false);
      return;
    }

    setChecking(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await checkUsername(clean);
        setAvailable(res.data.available);
      } catch {
        setAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 400);
  }, [user?.username]);

  async function handleSave(e) {
    e.preventDefault();
    const clean = username.toLowerCase().trim();
    if (clean.length < 3) return;
    setSaving(true);
    try {
      const res = await updateUsername(clean);
      setUser(prev => ({ ...prev, username: res.data.username }));
      setAvailable(null);
      toast.success('Username updated!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update username');
    } finally {
      setSaving(false);
    }
  }

  const clean = username.toLowerCase().trim();
  const valid = clean.length >= 3 && /^[a-z0-9_]+$/.test(clean);
  const changed = clean !== (user?.username || '');

  return (
    <div className="card mb-lg">
      <h2 className="section-header__title" style={{ marginBottom: '4px' }}>USERNAME</h2>
      <p className="text-muted" style={{ fontSize: '13px', marginBottom: '16px' }}>
        Your username is shown to friends on leaderboards and profiles.
      </p>
      <form onSubmit={handleSave} className="username-form">
        <div className="username-form__input-wrap">
          <span className="username-form__prefix">@</span>
          <input
            value={username}
            onChange={e => { setUsernameVal(e.target.value); checkAvailability(e.target.value); }}
            placeholder="choose_a_username"
            className="username-form__input"
            maxLength={20}
          />
          {checking && <span className="spinner spinner--sm username-form__spinner" />}
          {!checking && available === true && changed && <Check size={16} className="username-form__available" />}
          {!checking && available === false && <span className="username-form__taken">taken</span>}
        </div>
        {clean.length > 0 && clean.length < 3 && (
          <p className="username-form__hint">At least 3 characters</p>
        )}
        {clean.length >= 3 && !/^[a-z0-9_]+$/.test(clean) && (
          <p className="username-form__hint">Letters, numbers, and underscores only</p>
        )}
        <button
          type="submit"
          className="btn btn--primary"
          disabled={saving || !valid || !changed || available === false}
        >
          {saving ? <span className="spinner spinner--sm" /> : user?.username ? 'UPDATE' : 'SET USERNAME'}
        </button>
      </form>
    </div>
  );
}

export default function Settings() {
  const [stravaConnected, setStravaConnected] = useState(false);
  const [backfillComplete, setBackfillComplete] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    getStravaStatus()
      .then(res => {
        setStravaConnected(res.data.connected);
        setBackfillComplete(res.data.backfillComplete);
      })
      .catch(console.error)
      .finally(() => setLoading(false));

    return () => { if (eventSourceRef.current) eventSourceRef.current.close(); };
  }, []);

  function handleSync() {
    if (eventSourceRef.current) eventSourceRef.current.close();
    setSyncing(true);
    setSyncResult(null);
    setSyncProgress({ phase: 'fetching', message: 'Connecting...', processed: 0, total: 0 });

    eventSourceRef.current = syncStravaSSE((event, data) => {
      if (event === 'status') {
        setSyncProgress(prev => ({ ...prev, ...data }));
      } else if (event === 'progress') {
        setSyncProgress(prev => ({ ...prev, phase: 'processing', ...data }));
      } else if (event === 'complete') {
        setSyncing(false);
        setSyncProgress(null);
        setSyncResult(data);
        setBackfillComplete(true);
      } else if (event === 'error') {
        setSyncing(false);
        setSyncProgress(null);
        setSyncResult({
          error: data.error,
          rateLimited: data.rateLimited,
          processed: data.processed,
          matched: data.matched,
        });
      }
    });
  }

  async function handleStravaConnect() {
    const res = await getStravaAuthUrl();
    window.location.href = res.data.authUrl;
  }

  return (
    <div className="fade-in settings">
      <h1 className="page-header__title mb-sm">SETTINGS</h1>
      <p className="page-header__subtitle" style={{ marginBottom: '32px' }}>Configure your integrations.</p>

      <UsernameSection />

      {/* Strava Card */}
      <div className="card mb-lg">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <h2 className="section-header__title" style={{ marginBottom: '4px' }}>STRAVA</h2>
            <p className="text-muted" style={{ fontSize: '13px' }}>
              {backfillComplete
                ? 'New rides are synced automatically. You can re-sync to catch anything missed.'
                : 'Connect Strava to automatically mark trails as completed after your rides.'}
            </p>
          </div>
          <div className={`strava-status ${stravaConnected ? 'strava-status--connected' : 'strava-status--disconnected'}`}>
            {loading ? '...' : stravaConnected ? '● CONNECTED' : '○ NOT CONNECTED'}
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', color: 'var(--text-muted)', fontSize: '13px' }}>
            <span className="spinner" style={{ width: 16, height: 16 }} /> Checking Strava connection...
          </div>
        ) : !stravaConnected ? (
          <button className="strava-connect-btn" onClick={handleStravaConnect}>
            <img src="/strava-connect.svg" alt="Connect with Strava" style={{ height: '32px' }} />
          </button>
        ) : syncing ? (
          <SyncProgress progress={syncProgress} />
        ) : (
          <div>
            {backfillComplete && (
              <div className="live-sync-banner">
                <Zap size={14} />
                Live sync active — new rides are matched automatically
              </div>
            )}
            <button className="btn btn--primary btn--full" onClick={handleSync}>
              <RefreshCw size={14} style={{ marginRight: '6px' }} />
              {backfillComplete ? 'Re-sync Rides' : 'Sync Ride History'}
            </button>
            <p style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
              {backfillComplete
                ? 'Re-check your full ride history against your trail database.'
                : 'Import your last 2 years of rides and match against your trails.'}
            </p>
          </div>
        )}

        <div className="text-center" style={{ marginTop: '12px' }}>
          <img src="/powered-by-strava.svg" alt="Powered by Strava" style={{ height: '16px', opacity: 0.7 }} />
        </div>

        {syncResult && (
          <div className={`sync-result ${syncResult.error ? 'sync-result--error' : 'sync-result--success'}`}>
            {syncResult.error ? (
              <>
                <p style={{ color: syncResult.rateLimited ? 'var(--orange)' : 'var(--red)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: syncResult.processed ? '8px' : 0 }}>
                  <AlertCircle size={14} /> {syncResult.error}
                </p>
                {syncResult.processed > 0 && (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    Processed {syncResult.processed} ride{syncResult.processed !== 1 ? 's' : ''} before stopping.
                    {syncResult.matched?.length > 0 && ` Matched ${syncResult.matched.length} trail${syncResult.matched.length !== 1 ? 's' : ''}.`}
                  </p>
                )}
              </>
            ) : (
              <>
                <p style={{ fontWeight: 500, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Check size={14} /> Checked {syncResult.activitiesChecked} rides
                  {syncResult.skipped > 0 && <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '12px' }}>({syncResult.skipped} ruled out, {syncResult.apiCalls} detailed)</span>}
                </p>
                {syncResult.matched?.length > 0 ? (
                  <>
                    <p style={{ color: 'var(--accent)', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Trophy size={14} /> Auto-completed {syncResult.matched.length} trail{syncResult.matched.length !== 1 ? 's' : ''}:
                    </p>
                    {syncResult.matched.map((m) => (
                      <div key={`${m.systemName}-${m.trailName}`} style={{ fontSize: '13px', color: 'var(--text-muted)', paddingLeft: '12px' }}>
                        • {m.trailName} ({m.systemName})
                      </div>
                    ))}
                  </>
                ) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No new trails matched.</p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Trail Data Card */}
      <div className="card">
        <h2 className="section-header__title mb-sm">TRAIL DATA</h2>
        <p className="text-muted" style={{ fontSize: '13px' }}>
          Trail search and import is powered by OpenStreetMap. No API key required.
        </p>
      </div>
    </div>
  );
}
